import { create } from 'zustand';

import {
  API,
  deleteRequest,
  getRequest,
  getRequestBlob,
  patchRequest,
  postRequest,
  putRequest,
} from '@/api/api.ts';
import type {
  CreateInvoicePayload,
  Invoice,
} from '@/store/expenses-store/invoices/invoices.dto.ts';
import { downloadBlob } from '@/utils/download-file-from-link.ts';
import { generateSearchParams } from '@/utils/generate-search-params.ts';
import { toast } from '@/utils/toast.ts';

type InvoiceState = {
  invoices: Invoice[];
  invoice: Invoice | null;
  getInvoices: (options?: { taskId?: string }) => Promise<void>;
  getInvoice: (invoiceId: string) => Promise<void>;
  createInvoice: (body: CreateInvoicePayload) => Promise<void>;
  exportInvoices: () => Promise<void>;
  updateInvoice: (invoiceId: string, body: Invoice) => Promise<void>;
  setInvoiceStatus: (invoiceId: string, status: string) => Promise<void>;
  removeInvoice: (invoiceId: string) => Promise<void>;
  loading: boolean;
  exportLoading: boolean;
  error: string | null;
};

export const useInvoice = create<InvoiceState>((set, get) => ({
  invoices: [],
  invoice: null,
  loading: false,
  exportLoading: false,
  error: null,
  getInvoices: async (options) => {
    try {
      const searchParams = generateSearchParams({
        limit: 1000,
        page: 1,
        task_id: options?.taskId,
      });
      set({ loading: true, error: null });
      const response = await getRequest<{ items: Invoice[] }>(
        API.EXPENSES,
        `/invoice?${searchParams}`,
      );

      set({
        invoices: response.data.items,
        loading: false,
        error: null,
      });
    } catch {
      toast.error('Couldn’t load the invoices. Please try again.');
    } finally {
      set({ loading: false });
    }
  },
  getInvoice: async (invoiceId: string) => {
    try {
      set({ loading: true, error: null });
      const response = await getRequest<Invoice>(API.EXPENSES, `/invoice/${invoiceId}`);

      set({
        invoice: response.data,
        loading: false,
        error: null,
      });
    } catch {
      toast.error('Couldn’t load the invoice. Please try again.');
    } finally {
      set({ loading: false });
    }
  },
  exportInvoices: async () => {
    if (get().exportLoading) return;
    set({ exportLoading: true });
    try {
      const blob = await getRequestBlob(API.EXPENSES, `/invoice/export`);
      await downloadBlob(blob, `invoices-${new Date().toISOString().slice(0, 10)}.csv`);

      toast.success('Your invoices have been exported.');
    } catch {
      toast.error('Couldn’t export the invoices. Please try again.');
    } finally {
      set({ exportLoading: false });
    }
  },
  createInvoice: async (body: CreateInvoicePayload) => {
    try {
      const response = await postRequest<Invoice, CreateInvoicePayload>(
        API.EXPENSES,
        '/invoice',
        body,
      );

      set((state) => ({
        invoice: response.data,
        invoices: state.invoices ? [response.data, ...state.invoices] : state.invoices,
      }));
      toast.success('Invoice created');
      void get().getInvoices();
    } catch {
      toast.error('Couldn’t create the invoice. Please check the details.');
    }
  },
  updateInvoice: async (invoiceId: string, body: Invoice) => {
    try {
      set({ error: null });
      const response = await putRequest<Invoice, Invoice>(
        API.EXPENSES,
        `/invoice/${invoiceId}`,
        body,
      );

      set({
        invoice: response.data,
        error: null,
      });
      void get().getInvoices();
    } catch {
      toast.error('Couldn’t update the invoice. Please try again.');
    }
  },
  setInvoiceStatus: async (invoiceId: string, status: string) => {
    try {
      set({ error: null });
      const response = await patchRequest<Invoice, { status: string }>(
        API.EXPENSES,
        `/invoice/${invoiceId}/status`,
        { status },
      );

      set({
        invoice: response.data,
        error: null,
      });
      toast.success('Invoice status updated');
      void get().getInvoices();
    } catch {
      toast.error('Couldn’t update the invoice status. Please try again.');
    }
  },
  removeInvoice: async (invoiceId: string) => {
    try {
      set({ error: null });
      await deleteRequest<void>(API.EXPENSES, `/invoice/${invoiceId}`);

      set((state) => ({
        invoices: state.invoices
          ? state.invoices.filter(
              (invoice) =>
                invoice.document_id !== invoiceId && invoice.invoice_number !== invoiceId,
            )
          : state.invoices,
        error: null,
      }));
      toast.success('Invoice deleted');
      void get().getInvoices();
    } catch {
      toast.error('Couldn’t delete the invoice. Please try again.');
    }
  },
}));
